import { useEffect, useRef, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import { captureError } from '../../../utils/sentryHelpers';
import { buildDuplicateMediaItem } from '../../../utils/mediathek';
import { GET_MEDIA_ITEM } from '../mediathekGraphql';

const client = generateClient();

/**
 * Prefill of the create form from a saved item (?copyFrom=<id>).
 *
 * Fetches the SAVED source with GET_MEDIA_ITEM (never the in-memory form of
 * another page) and calls setItem(buildDuplicateMediaItem(source, targetSeriesId))
 * exactly once per copyFrom id, after the data hook's own create reset
 * (`ready` = create mode and not loading). `copyVersion` is bumped in the same
 * state batch as the prefill, so it can key the dirty snapshot and the
 * description editor.
 *
 * @param {{copyFrom?: string, ready: boolean, targetSeriesId?: string, setItem: (item: object) => void}} options
 * @returns {{status: 'idle'|'loading'|'ready'|'error', source: {id: string, title: Array}|null, copyVersion: number}}
 */
export default function useCopySource({ copyFrom, ready, targetSeriesId, setItem }) {
  const [state, setState] = useState(() => ({ status: copyFrom ? 'loading' : 'idle', source: null }));
  const [copyVersion, setCopyVersion] = useState(0);
  const appliedRef = useRef(null);

  useEffect(() => {
    if (!copyFrom || !ready || appliedRef.current === copyFrom) return undefined;
    appliedRef.current = copyFrom;
    let cancelled = false;
    let finished = false;
    setState({ status: 'loading', source: null });

    const run = async () => {
      try {
        const response = await client.graphql({ query: GET_MEDIA_ITEM, variables: { id: copyFrom } });
        if (cancelled) return;
        const source = response?.data?.getMediaItem;
        if (!source) {
          captureError(new Error('Mediathek copy source not found'), {
            feature: 'mediathek',
            operation: 'fetch-copy-source',
            context: { copyFrom, notFound: true },
            tags: { component: 'EditMediaItem' },
          });
          setState({ status: 'error', source: null });
          return;
        }
        // Same batch: prefill + new dirty snapshot key
        setItem(buildDuplicateMediaItem(source, targetSeriesId));
        setCopyVersion((v) => v + 1);
        setState({ status: 'ready', source: { id: source.id, title: source.title || [] } });
      } catch (error) {
        if (cancelled) return;
        console.error('Mediathek item: loading the copy source failed', error);
        captureError(error, {
          feature: 'mediathek',
          operation: 'fetch-copy-source',
          context: { copyFrom },
          tags: { component: 'EditMediaItem' },
        });
        setState({ status: 'error', source: null });
      } finally {
        finished = true;
      }
    };
    run();

    return () => {
      cancelled = true;
      // Interrupted before the prefill (e.g. effect re-run): allow a new attempt
      if (!finished && appliedRef.current === copyFrom) appliedRef.current = null;
    };
  }, [copyFrom, ready, targetSeriesId, setItem]);

  return { ...state, copyVersion };
}
